import React, { useEffect, useRef, useState } from 'react';

// Champs modifiables sur place : ils ressemblent à du texte, s'éditent au clic, s'enregistrent en quittant le champ
// (ou sur Entrée) et Échap annule. readOnly : le même rendu, sans édition (domaine tenu par un autre rôle).

export const InlineText = ({
  value, onSave, placeholder = 'À préciser', className = '', required = false, ariaLabel, autoSize = false, readOnly = false,
}) => {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  const commit = () => {
    const next = draft.trim();
    if (required && !next) { setDraft(value || ''); return; }
    if (next !== (value || '')) onSave(next);
  };
  const look = 'rounded-md border border-transparent px-1 -mx-1';
  const input = (
    <input value={draft} placeholder={placeholder} aria-label={ariaLabel || placeholder} size={autoSize ? 1 : undefined}
      readOnly={readOnly} onChange={(e) => setDraft(e.target.value)} onBlur={readOnly ? undefined : commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') { setDraft(value || ''); setTimeout(() => e.target.blur()); }
      }}
      className={`min-w-0 bg-transparent outline-none transition placeholder:text-slate-300 ${readOnly ? 'cursor-default' : 'hover:border-slate-200 focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-50'} ${look} ${className} ${autoSize ? 'col-start-1 row-start-1 w-full' : ''}`} />
  );
  if (!autoSize) return input;
  // Largeur du texte : un double invisible occupe la même case de grille que le champ.
  return (
    <span className="inline-grid max-w-full">
      <span aria-hidden className={`invisible col-start-1 row-start-1 whitespace-pre ${look} ${className}`}>{draft || placeholder}{' '}</span>
      {input}
    </span>
  );
};

export const InlineArea = ({ value, onSave, placeholder = 'À préciser', className = '', rows = 2, readOnly = false }) => {
  const [draft, setDraft] = useState(value || '');
  const ref = useRef(null);
  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => {
    const node = ref.current;
    if (node) { node.style.height = 'auto'; node.style.height = `${node.scrollHeight + 2}px`; }
  }, [draft]);
  // Hauteur reprise quand la largeur change (champ mesuré avant la mise en page : il restait immense).
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    let width = node.clientWidth;
    const observer = new ResizeObserver(() => {
      if (node.clientWidth === width) return;
      width = node.clientWidth;
      node.style.height = 'auto';
      node.style.height = `${node.scrollHeight + 2}px`;
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return (
    <textarea ref={ref} value={draft} rows={rows} placeholder={placeholder} readOnly={readOnly}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { if (!readOnly && draft.trim() !== (value || '').trim()) onSave(draft.trim()); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); }
        if (e.key === 'Escape') { setDraft(value || ''); setTimeout(() => e.target.blur()); }
      }}
      className={`block w-full resize-none rounded-lg border border-transparent bg-transparent px-2 py-1 -mx-2 text-sm leading-relaxed text-slate-700 outline-none transition placeholder:text-slate-400 ${readOnly ? 'cursor-default' : 'hover:border-slate-200 focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-50'} ${className}`} />
  );
};

// Une date saisie au clavier passe par des années partielles (« 0002-… ») : seules les dates plausibles partent.
const plausible = (text) => /^\d{4}-\d{2}-\d{2}$/.test(text) && Number(text.slice(0, 4)) >= 2000;
const DATE_INPUT = 'rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-xs tabular-nums text-slate-700 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50 disabled:border-transparent disabled:bg-transparent';

/** `deferred` : enregistre en quittant le champ (ou sur Entrée) plutôt qu'à chaque date valide tapée — quand un
 * changement demande un motif, la fenêtre ne s'ouvre pas au milieu de la saisie. */
export const DateField = ({ value, onSave, ariaLabel, className = '', readOnly = false, deferred = false }) => {
  const current = (value || '').slice(0, 10);
  const [draft, setDraft] = useState(current);
  useEffect(() => { setDraft(current); }, [current]);
  const commit = () => {
    if (!plausible(draft)) setDraft(current);
    else if (draft !== current) onSave(draft);
  };
  return (
    <input type="date" value={draft} aria-label={ariaLabel} disabled={readOnly}
      onChange={(e) => {
        setDraft(e.target.value);
        if (!deferred && plausible(e.target.value) && e.target.value !== current) onSave(e.target.value);
      }}
      onKeyDown={deferred ? (e) => { if (e.key === 'Enter') e.currentTarget.blur(); } : undefined}
      onBlur={() => { if (deferred) commit(); else if (!plausible(draft)) setDraft(current); }}
      className={`${DATE_INPUT} ${className}`} />
  );
};

/** Début → fin d'une période. `deferred` (un motif sera demandé) : on règle les deux dates, puis un seul
 * « Enregistrer » (ou Entrée) envoie les deux ensemble — une seule fenêtre de motif. Sinon, chaque date part seule. */
export const DateRangeField = ({ start, end, onSave, labels = ['Début', 'Fin'], readOnly = false, deferred = false }) => {
  const current = [(start || '').slice(0, 10), (end || '').slice(0, 10)];
  const [draft, setDraft] = useState(current);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft([current[0], current[1]]); }, [current[0], current[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!deferred) {
    return (
      <>
        <DateField value={start} ariaLabel={labels[0]} readOnly={readOnly} onSave={(v) => onSave({ start_date: v })} />
        →
        <DateField value={end} ariaLabel={labels[1]} readOnly={readOnly} onSave={(v) => onSave({ end_date: v })} />
      </>
    );
  }
  const fields = {};
  if (draft[0] !== current[0]) fields.start_date = draft[0];
  if (draft[1] !== current[1]) fields.end_date = draft[1];
  const changed = Object.keys(fields).length > 0;
  const valid = draft.every(plausible) && draft[0] <= draft[1];
  const save = async () => {
    if (!changed || !valid || saving) return;
    setSaving(true);
    try {
      await onSave(fields); // annulé dans la fenêtre du motif : les dates restent en attente, prêtes à renvoyer
    } finally {
      setSaving(false);
    }
  };
  const input = (index) => (
    <input type="date" value={draft[index]} aria-label={labels[index]} disabled={readOnly || saving}
      onChange={(e) => { const value = e.target.value; setDraft((d) => (index ? [d[0], value] : [value, d[1]])); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); save(); }
        if (e.key === 'Escape') setDraft([current[0], current[1]]);
      }}
      className={`${DATE_INPUT} ${changed && draft[index] !== current[index] ? 'border-indigo-300 bg-indigo-50/40' : ''}`} />
  );
  return (
    <>
      {input(0)}
      →
      {input(1)}
      {changed && (
        <span className="flex items-center gap-1">
          <button type="button" onClick={save} disabled={!valid || saving}
            title={valid ? undefined : 'La fin doit venir après le début'}
            className="rounded-md bg-indigo-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
            Enregistrer
          </button>
          <button type="button" onClick={() => setDraft([current[0], current[1]])} disabled={saving}
            className="rounded-md px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800">
            Annuler
          </button>
        </span>
      )}
    </>
  );
};
